/**
 * PrototypePill — inline chip rendered under an assistant message when
 * the SSE stream produced a `prototype_ready` event. Mirrors
 * {@link ArtifactPill} in style so the two indicators sit side-by-side
 * without visual dissonance.
 *
 * Clicking the pill re-opens the prototyper panel — for the initial turn
 * the panel is already auto-opened by the headers facade; the pill's
 * value is that it re-opens the panel on later visits (history load) and
 * after the user manually closed it.
 *
 * Layout matches the artifact pill exactly: the prototype's name on the first line, its kind
 * ("Prototype") on a grey second line. Both replaced the old single line, which read
 * "Active prototype — {name}" and so spent the two words the founder scans for a name on a
 * state the border already shows.
 */

import React from 'react';
import { Play, Rocket } from 'lucide-react';
import { cn } from '../../lib/utils';
import Icon from './Icon';
import { Button } from '../atoms/button';

export interface PrototypePillProps {
  /** Called when the user clicks the pill. Host owns the panel-open flow. */
  onOpen?: () => void;
  /** RTL from the parent message. */
  isRtl?: boolean;
  /** Extra classes for positioning inside MessageItem. */
  className?: string;
  /**
   * The prototype's name, with its version suffix — resolve it with
   * `resolvePrototypeTitle` from `@ideoz/core` so every pill agrees.
   */
  title?: string;
  /** The kind line under the name. */
  kindLabel?: string;
  /** Highlight when this version is the one loaded in the workbench. */
  isActive?: boolean;
  /** Show a loading state while restoring this version. */
  isLoading?: boolean;
  /** Text shown while `isLoading` (e.g. "Building…" during a live build). */
  loadingLabel?: string;
  /**
   * The turn's build stopped before it passed (Stop, a dropped connection, a closed tab, a
   * failed build). The kind line then says so, and a Continue button appears beside it.
   */
  didNotFinish?: boolean;
  /** Continue button on an unfinished build. Without it the button is not shown. */
  onContinue?: () => void;
}

export const PrototypePill: React.FC<PrototypePillProps> = ({
  onOpen,
  isRtl,
  className,
  title,
  kindLabel = 'Prototype',
  isActive = false,
  isLoading = false,
  loadingLabel = 'Restoring…',
  didNotFinish = false,
  onContinue,
}) => {
  const showUnfinished = didNotFinish && !isLoading;
  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border bg-white w-full',
        isActive ? 'border-primary-400 ring-1 ring-primary-200' : 'border-gray-200',
        'dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      data-testid="prototype-pill"
    >
      <div className="flex items-center w-full">
        <button
          type="button"
          onClick={() => onOpen?.()}
          disabled={isLoading}
          // The active prototype is the one the workbench is showing, which is what
          // `aria-current` means; the ring and the dot below are its visual half.
          aria-current={isActive ? 'true' : undefined}
          className={cn(
            'flex flex-1 min-w-0 items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-200 px-6 py-6',
            onOpen && !isLoading && 'hover:text-primary-600 dark:hover:text-primary-400',
            isLoading && 'opacity-70 cursor-wait'
          )}
          data-testid="prototype-pill-open"
        >
          <Icon icon={Rocket} size={32} strokeWidth={1} className="shrink-0 text-primary-500" />
          {/* min-w-0 so a long name ellipsises instead of stretching the pill past the message. */}
          <div className="flex flex-col min-w-0">
            {/* `title` also as the tooltip: names run to a sentence on prototypes built
                before the pill had a real one. */}
            <span className="truncate" title={title || undefined} data-testid="prototype-pill-title">
              {title || 'Prototype'}
            </span>
            <span
              className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500"
              data-testid="prototype-pill-kind"
            >
              {isActive && !isLoading && (
                // A dot, not the word "Active": it reads with the ring rather than instead of it.
                <span
                  className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
                  aria-hidden="true"
                  data-testid="prototype-pill-active-dot"
                />
              )}
              {/* Loading replaces the kind line, never the name, so a restoring pill still
                  says which prototype is restoring. */}
              {showUnfinished ? (
                <span
                  className="truncate text-amber-600 dark:text-amber-400"
                  data-testid="prototype-pill-unfinished"
                >
                  Didn&apos;t finish
                </span>
              ) : (
                <span className="truncate">{isLoading ? loadingLabel : kindLabel}</span>
              )}
            </span>
          </div>
        </button>
        {showUnfinished && onContinue && (
          // A sibling of the open button, not inside it: a button cannot hold another button.
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onContinue}
            className={cn('shrink-0', isRtl ? 'ml-6' : 'mr-6')}
            data-testid="prototype-pill-continue"
          >
            <Play size={14} aria-hidden="true" />
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};

export default PrototypePill;
